import React, { useEffect, useState } from "react";
import { Users, Plus, KeyRound, AlertCircle, CheckCircle2, RefreshCw } from "lucide-react";
import TiltCard from "../components/TiltCard";
import { colleges } from "../data/mock";
import { apiUrl, hasApi, SIGNIN_DISCONNECTED, authHeaders, SESSION_EXPIRED_MESSAGE, clearClientSession, isAuthFailure } from "../lib/api";

const ROLES = [
  { value: "department", label: "Department user" },
  { value: "principal", label: "Principal" },
  { value: "admin", label: "Administrator" },
];

const TAB_FIELDS = [
  { key: "circulars", label: "Circulars" },
  { key: "gallery", label: "Gallery" },
  { key: "careers", label: "Careers" },
];

const emptyTabs = () => ({ circulars: "view", gallery: "view", careers: "view" });

function TabSelect({ username, tab, value, onChange, testId }) {
  return (
    <select
      data-testid={testId}
      className="input-3d"
      value={value || "none"}
      onChange={(e) => onChange(username, tab, e.target.value)}
      style={{ width: "auto", padding: "6px 8px", fontSize: 12 }}
      aria-label={`${tab} access`}
    >
      <option value="none">none</option>
      <option value="view">view</option>
      <option value="edit">edit</option>
    </select>
  );
}

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    username: "",
    password: "",
    name: "",
    role: "department",
    college: colleges[0].slug,
    privilege: "view",
    tabs: emptyTabs(),
  });
  const [resetFor, setResetFor] = useState("");
  const [resetPwd, setResetPwd] = useState("");

  const load = async () => {
    if (!hasApi()) { setStatus("error"); setError(SIGNIN_DISCONNECTED); return; }
    setStatus("loading");
    try {
      const r = await fetch(apiUrl("/api/auth/users"), { headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (isAuthFailure(r.status, d.detail)) {
          clearClientSession();
          throw new Error(SESSION_EXPIRED_MESSAGE);
        }
        throw new Error(d.detail || "Could not load the user list.");
      }
      setUsers(d.users || []);
      setStatus("ready");
    } catch (e) {
      setStatus("error");
      setError(e.message === "Failed to fetch" ? SIGNIN_DISCONNECTED : (e.message || SIGNIN_DISCONNECTED));
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setNote(null);
    if (!hasApi()) { setNote({ ok: false, text: SIGNIN_DISCONNECTED }); return; }
    setBusy(true);
    try {
      const body = {
        username: form.username.trim(),
        password: form.password,
        name: form.name.trim(),
        role: form.role,
        privilege: form.privilege,
        tabs: form.role === "admin" ? { circulars: "edit", gallery: "edit", careers: "edit" } : form.tabs,
      };
      if (form.role !== "admin") body.college = form.college;
      const r = await fetch(apiUrl("/api/auth/users"), {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (isAuthFailure(r.status, d.detail)) {
          clearClientSession();
          throw new Error(SESSION_EXPIRED_MESSAGE);
        }
        throw new Error(d.detail || "Could not create the user.");
      }
      setNote({ ok: true, text: `Account ${d.user.username} has been created. Share the password privately.` });
      setForm({ username: "", password: "", name: "", role: "department", college: colleges[0].slug, privilege: "view", tabs: emptyTabs() });
      await load();
    } catch (err) {
      setNote({ ok: false, text: err.message });
    }
    setBusy(false);
  };

  const setTabAccess = async (username, tab, level) => {
    setNote(null);
    const current = users.find((u) => u.username === username);
    const tabs = { ...(current?.tabs || emptyTabs()), [tab]: level };
    const r = await fetch(apiUrl(`/api/auth/users/${encodeURIComponent(username)}`), {
      method: "PATCH",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ tabs }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      if (isAuthFailure(r.status, d.detail)) {
        clearClientSession();
        setNote({ ok: false, text: SESSION_EXPIRED_MESSAGE });
        return;
      }
      setNote({ ok: false, text: d.detail || "Could not update access." });
      return;
    }
    setUsers((list) => list.map((u) => (u.username === username ? d.user : u)));
    setNote({ ok: true, text: `${username} now has ${level} access on ${tab}.` });
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setNote(null);
    if (!resetFor.trim()) { setNote({ ok: false, text: "Choose a username to reset." }); return; }
    setBusy(true);
    try {
      const body = { username: resetFor.trim() };
      if (resetPwd.trim()) body.new_password = resetPwd.trim();
      const r = await fetch(apiUrl("/api/auth/admin-reset"), {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (isAuthFailure(r.status, d.detail)) {
          clearClientSession();
          throw new Error(SESSION_EXPIRED_MESSAGE);
        }
        throw new Error(d.detail || "Reset failed.");
      }
      let text = d.detail || "Password reset.";
      if (d.temporary_password) {
        text += ` Temporary password: ${d.temporary_password}`;
        if (!d.email) text += " This office has no email on the website — share the password directly.";
      } else if (d.mailed && d.email) {
        text = `A temporary password was sent to ${d.email}.`;
      }
      setNote({ ok: true, text });
      setResetPwd("");
      await load();
    } catch (err) {
      setNote({ ok: false, text: err.message });
    }
    setBusy(false);
  };

  return (
    <TiltCard intensity={2}>
      <div style={{ padding: 26 }} data-testid="users-admin">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: "#1C1917", display: "flex", alignItems: "center", gap: 8 }}>
            <Users size={20} color="#F97316" /> Users
          </div>
          <button type="button" onClick={load} className="btn-outline-3d" style={{ padding: "6px 12px", fontSize: 12 }} data-testid="users-refresh-btn">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <p style={{ fontSize: 13, color: "#57534E", marginTop: 8, lineHeight: 1.55 }}>
          Create department accounts and set <b>none / view / edit</b> for Circulars, Gallery and Careers.
          A reset is emailed only when that office already has an address on this website. Piduguralla has none.
        </p>

        {status === "loading" && (
          <div data-testid="users-loading" style={{ marginTop: 14, fontSize: 13, color: "#78716C", fontWeight: 600 }}>Loading users…</div>
        )}
        {status === "error" && (
          <div data-testid="users-error" style={{ marginTop: 14, padding: "12px 14px", background: "#FEF2F2", border: "1px solid rgba(220,38,38,0.28)", borderRadius: 12, color: "#B91C1C", fontSize: 13, display: "flex", gap: 8 }}>
            <AlertCircle size={16} style={{ flexShrink: 0 }} /> {error}
          </div>
        )}
        {status === "ready" && !users.length && (
          <div data-testid="users-empty" style={{ marginTop: 14, fontSize: 13, color: "#78716C" }}>No users have been created yet.</div>
        )}

        {status === "ready" && users.length > 0 && (
          <>
            <div className="users-table-wrap" data-testid="users-table" style={{ marginTop: 16, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 760 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#EA580C", fontSize: 11, letterSpacing: 0.6 }}>
                    <th style={{ padding: "8px 6px" }}>USERNAME</th>
                    <th style={{ padding: "8px 6px" }}>NAME</th>
                    <th style={{ padding: "8px 6px" }}>CIRCULARS</th>
                    <th style={{ padding: "8px 6px" }}>GALLERY</th>
                    <th style={{ padding: "8px 6px" }}>CAREERS</th>
                    <th style={{ padding: "8px 6px" }}>EMAIL</th>
                    <th style={{ padding: "8px 6px" }}>PASSWORD</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.username} data-testid={`user-row-${u.username}`} style={{ borderTop: "1px solid rgba(249,115,22,0.18)" }}>
                      <td style={{ padding: "10px 6px", fontWeight: 700, color: "#1C1917" }}>{u.username}</td>
                      <td style={{ padding: "10px 6px", color: "#44403C" }}>{u.name}<div style={{ fontSize: 11, color: "#78716C" }}>{u.role}{u.college ? ` · ${u.college}` : ""}</div></td>
                      {u.role === "admin" ? (
                        <td style={{ padding: "10px 6px" }} colSpan={3}><span className="badge badge-yellow">all tabs · edit</span></td>
                      ) : TAB_FIELDS.map((t) => (
                        <td key={t.key} style={{ padding: "10px 6px" }}>
                          <TabSelect username={u.username} tab={t.key} value={u.tabs?.[t.key]} onChange={setTabAccess} testId={`user-tab-${t.key}-${u.username}`} />
                        </td>
                      ))}
                      <td style={{ padding: "10px 6px", color: "#57534E", wordBreak: "break-all" }}>{u.email || "—"}</td>
                      <td style={{ padding: "10px 6px", color: u.has_password ? "#15803D" : "#B45309", fontWeight: 700 }}>{u.has_password ? "set" : "not set"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="users-card-list" data-testid="users-cards">
              {users.map((u) => (
                <div key={u.username} style={{ marginTop: 12, padding: 14, border: "1px solid rgba(249,115,22,0.22)", borderRadius: 12, background: "#fff" }}>
                  <div style={{ fontWeight: 800, color: "#1C1917" }}>{u.username}</div>
                  <div style={{ fontSize: 12.5, color: "#57534E", marginTop: 4 }}>{u.name} · {u.role}{u.college ? ` · ${u.college}` : ""}</div>
                  <div style={{ fontSize: 12.5, color: "#57534E", marginTop: 4 }}>{u.email || "No email on the website"} · password {u.has_password ? "set" : "not set"}</div>
                  {u.role !== "admin" && (
                    <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                      {TAB_FIELDS.map((t) => (
                        <label key={t.key} style={{ fontSize: 12, fontWeight: 700, color: "#9A3412" }}>
                          {t.label}
                          <div style={{ marginTop: 4 }}>
                            <TabSelect username={u.username} tab={t.key} value={u.tabs?.[t.key]} onChange={setTabAccess} testId={`user-tab-mobile-${t.key}-${u.username}`} />
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <form onSubmit={create} data-testid="users-create-form" style={{ marginTop: 22, paddingTop: 18, borderTop: "1px solid rgba(249,115,22,0.2)" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1C1917", display: "flex", alignItems: "center", gap: 8 }}><Plus size={16} color="#F97316" /> Create user</div>
          <div className="users-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 }}>
            <input required className="input-3d" placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="users-create-username" />
            <input required minLength={8} type="text" className="input-3d" placeholder="Password (min 8 characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="users-create-password" />
            <input required className="input-3d" placeholder="Full name / office" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="users-create-name" style={{ gridColumn: "1 / -1" }} />
            <select className="input-3d" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="users-create-role">
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            {form.role !== "admin" && TAB_FIELDS.map((t) => (
              <label key={t.key} style={{ fontSize: 12, fontWeight: 700, color: "#9A3412" }}>
                {t.label} access
                <select
                  className="input-3d"
                  value={form.tabs[t.key]}
                  onChange={(e) => setForm({ ...form, tabs: { ...form.tabs, [t.key]: e.target.value }, privilege: Object.values({ ...form.tabs, [t.key]: e.target.value }).includes("edit") ? "edit" : "view" })}
                  data-testid={`users-create-tab-${t.key}`}
                  style={{ marginTop: 4 }}
                >
                  <option value="none">none</option>
                  <option value="view">view</option>
                  <option value="edit">edit</option>
                </select>
              </label>
            ))}
            {form.role !== "admin" && (
              <select className="input-3d" value={form.college} onChange={(e) => setForm({ ...form, college: e.target.value })} data-testid="users-create-college" style={{ gridColumn: "1 / -1" }}>
                {colleges.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            )}
          </div>
          <button type="submit" className="btn-primary-3d" disabled={busy} data-testid="users-create-btn" style={{ marginTop: 12, width: "100%" }}>Create account</button>
        </form>

        <form onSubmit={resetPassword} data-testid="users-reset-form" style={{ marginTop: 22, paddingTop: 18, borderTop: "1px solid rgba(249,115,22,0.2)" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1C1917", display: "flex", alignItems: "center", gap: 8 }}><KeyRound size={16} color="#F97316" /> Reset password</div>
          <p style={{ fontSize: 12.5, color: "#57534E", marginTop: 6 }}>Only the administrator can reset a password. If that office has an email on this website, the new password is sent there.</p>
          <input list="apmercl-usernames" required className="input-3d" placeholder="Username" value={resetFor} onChange={(e) => setResetFor(e.target.value)} data-testid="users-reset-username" style={{ marginTop: 10 }} />
          <datalist id="apmercl-usernames">{users.map((u) => <option key={u.username} value={u.username} />)}</datalist>
          <input minLength={8} type="text" className="input-3d" placeholder="New password (leave blank to generate)" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} data-testid="users-reset-password" style={{ marginTop: 10 }} />
          <button type="submit" className="btn-red" disabled={busy} data-testid="users-reset-btn" style={{ marginTop: 12, width: "100%", borderRadius: 999 }}>Reset and notify</button>
        </form>

        {note && (
          <div data-testid="users-note" style={{ marginTop: 14, fontSize: 13.5, fontWeight: 600, color: note.ok ? "#15803D" : "#B91C1C", display: "flex", gap: 6, alignItems: "flex-start" }}>
            {note.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />} <span>{note.text}</span>
          </div>
        )}
      </div>
      <style>{`
        .users-card-list { display: none; }
        @media (max-width: 720px) {
          .users-table-wrap { display: none; }
          .users-card-list { display: block; }
          .users-form-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </TiltCard>
  );
}
